import { create } from 'zustand';
import type { IntercomConfig } from '@livepuls/types';
import { api } from '../lib/api.js';
import { getSocket, registerAsPanel } from '../lib/socket.js';

interface PanelStore {
  panelId: string | null;
  config: IntercomConfig | null;
  configLoaded: boolean;
  /** IDs der lokal aktuell vom User gedrückten Buttons (PPT/latching). */
  pressed: Set<string>;
  /** IDs der Buttons, die durch eine Remote-Action (Admin) aktiv sind. */
  remoteActive: Set<string>;
  /** Vom Admin (Remote) gesetzter Pegel pro Button-ID (0–100). */
  remoteVolume: Record<string, number>;
  /** Button-ID, die der optionale Hardware-Funktaster gerade steuert (grün). */
  hwTargetButtonId: string | null;
  online: boolean;
  loadConfig: () => Promise<void>;
  bind: (panelId: string) => void;
  setPressed: (id: string, on: boolean) => void;
  setHwTarget: (id: string | null) => void;
}

export const usePanelStore = create<PanelStore>((set, get) => ({
  panelId: null,
  config: null,
  configLoaded: false,
  pressed: new Set(),
  remoteActive: new Set(),
  remoteVolume: {},
  hwTargetButtonId: null,
  online: false,

  loadConfig: async () => {
    const res = await api.get<{ config: IntercomConfig | null }>('/api/v1/config/live');
    set({ config: res.config, configLoaded: true });
  },

  bind: (panelId) => {
    if (get().panelId === panelId) return;
    set({ panelId });
    registerAsPanel(panelId);

    const socket = getSocket();
    socket.on('connect', () => set({ online: true }));
    socket.on('disconnect', () => set({ online: false }));

    socket.on('config:update', (e) => {
      set({ config: e.config, configLoaded: true });
    });

    socket.on('admin:remote-action', (e) => {
      if (e.panelId !== get().panelId) return;
      const next = new Set(get().remoteActive);
      const { action } = e;
      if (action.kind === 'button-press') next.add(action.buttonId);
      else if (action.kind === 'button-release') next.delete(action.buttonId);
      else if (action.kind === 'button-toggle') {
        if (next.has(action.buttonId)) next.delete(action.buttonId);
        else next.add(action.buttonId);
      } else if (action.kind === 'button-volume') {
        // Admin stellt den Pegel dieses Buttons fern ein → PanelButton wendet
        // ihn live an (Audio) und zeigt den neuen Balken.
        set({ remoteVolume: { ...get().remoteVolume, [action.buttonId]: action.volume } });
        return;
      }
      set({ remoteActive: next });
    });
  },

  setPressed: (id, on) => {
    const next = new Set(get().pressed);
    if (on) next.add(id);
    else next.delete(id);
    set({ pressed: next });
  },

  setHwTarget: (id) => {
    if (get().hwTargetButtonId === id) return;
    set({ hwTargetButtonId: id });
  },
}));
